export type AuthErrorKind = "forgot-password" | "user-cancelled" | "unknown";

/// Classifies an MSAL/B2C auth error by the AADB2C error code embedded in its message.
/// B2C reports some benign user actions as errors on the redirect/popup return leg:
///  - AADB2C90118: the user clicked the "Forgot your password?" link on the sign-in page
///  - AADB2C90091: the user cancelled out of a self-asserted page (e.g. the password-reset flow)
/// See https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes
export const classifyAuthError = (error: { errorMessage?: string } | null | undefined): AuthErrorKind => {
    const message = error?.errorMessage ?? "";
    if (message.includes("AADB2C90118")) return "forgot-password";
    if (message.includes("AADB2C90091")) return "user-cancelled";
    return "unknown";
};

/// Determines whether an authentication result was issued by the password-reset policy rather than
/// the sign-in policy.  Our policies emit the issuing policy id in the `acr` claim (`tfp` is checked
/// defensively - it's the claim newer B2C configurations use), with the result's authority as a
/// fallback.  A password-reset token carries none of the application claims (roles, allowed_tags,
/// user_metadata), so it must never be adopted as the session.
export const isPasswordResetResult = (result: { idTokenClaims?: object, authority?: string } | null | undefined): boolean => {
    if (!result) return false;
    const claims = result.idTokenClaims as Record<string, unknown> | undefined;
    const policy = ((claims?.["acr"] ?? claims?.["tfp"]) as string | undefined)?.toLowerCase();
    return policy === "b2c_1a_passwordreset" || (result.authority?.toLowerCase()?.includes("b2c_1a_passwordreset") ?? false);
};

/// Tracks that the user has been sent into the B2C password-reset policy.  While set, the hard-coded
/// sign-in `authorityMetadata` must be suppressed: MSAL applies that single metadata blob to EVERY
/// authority used by a client, which would resolve the password-reset policy to the sign-in policy's
/// endpoints.  Stored in localStorage so it survives the full-page redirects to/from B2C; cleared once
/// the flow concludes (success, cancellation, or failure).
const PASSWORD_RESET_IN_PROGRESS_KEY = "auth.password-reset-in-progress";

export const isPasswordResetInProgress = () => localStorage.getItem(PASSWORD_RESET_IN_PROGRESS_KEY) !== null;
export const setPasswordResetInProgress = () => localStorage.setItem(PASSWORD_RESET_IN_PROGRESS_KEY, "true");
export const clearPasswordResetInProgress = () => localStorage.removeItem(PASSWORD_RESET_IN_PROGRESS_KEY);
